import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../utils/constants';

/**
 * Hook principal para gestionar el estado y operaciones de Transportistas
 * Para TRANSPORTISTA: filtra solo las entidades de sus propias empresas
 */
export const useTransportistas = (currentUser) => {
  // Data state
  const [companies, setCompanies] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Verificar si es transportista
  const isTransportista = currentUser?.role === 'TRANSPORTISTA';

  // Filtrar entidades por empresas del transportista
  const filterByTransportista = useCallback((data, companyList) => {
    if (!isTransportista || !currentUser?.id) return data;
    // Obtener IDs de empresas del transportista
    const myCompanyIds = companyList
      .filter(c => c.transportista_id === currentUser.id)
      .map(c => c.id);
    // Filtrar entidades que pertenecen a esas empresas
    return data.filter(item => myCompanyIds.includes(item.company_id));
  }, [isTransportista, currentUser?.id]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [companiesRes, trucksRes, trailersRes, driversRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/companies`),
        axios.get(`${API_URL}/trucks`),
        axios.get(`${API_URL}/trailers`),
        axios.get(`${API_URL}/drivers`),
        axios.get(`${API_URL}/users`).catch(() => ({ data: [] })) // TRANSPORTISTA puede no tener acceso
      ]);
      
      let companyList = companiesRes.data;
      
      // Si es TRANSPORTISTA, filtrar solo sus empresas
      if (isTransportista && currentUser?.id) {
        companyList = companyList.filter(c => c.transportista_id === currentUser.id);
      }
      
      setCompanies(companyList);
      setTrucks(filterByTransportista(trucksRes.data, companiesRes.data));
      setTrailers(filterByTransportista(trailersRes.data, companiesRes.data));
      setDrivers(filterByTransportista(driversRes.data, companiesRes.data));
      setUsers(usersRes.data.filter(u => u.role === 'TRANSPORTISTA'));
      setAdmins(usersRes.data.filter(u => u.role === 'ADMIN'));
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [isTransportista, currentUser?.id, filterByTransportista]);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [fetchData, currentUser]);

  // Refresh specific entity
  const refreshCompanies = useCallback(async () => {
    const res = await axios.get(`${API_URL}/companies`);
    let companyList = res.data;
    if (isTransportista && currentUser?.id) {
      companyList = companyList.filter(c => c.transportista_id === currentUser.id);
    }
    setCompanies(companyList);
  }, [isTransportista, currentUser?.id]);

  const refreshTrucks = useCallback(async () => {
    const [trucksRes, companiesRes] = await Promise.all([
      axios.get(`${API_URL}/trucks`),
      axios.get(`${API_URL}/companies`)
    ]);
    setTrucks(filterByTransportista(trucksRes.data, companiesRes.data));
  }, [filterByTransportista]);

  const refreshTrailers = useCallback(async () => {
    const [trailersRes, companiesRes] = await Promise.all([
      axios.get(`${API_URL}/trailers`),
      axios.get(`${API_URL}/companies`)
    ]);
    setTrailers(filterByTransportista(trailersRes.data, companiesRes.data));
  }, [filterByTransportista]);

  const refreshDrivers = useCallback(async () => {
    const [driversRes, companiesRes] = await Promise.all([
      axios.get(`${API_URL}/drivers`),
      axios.get(`${API_URL}/companies`)
    ]);
    setDrivers(filterByTransportista(driversRes.data, companiesRes.data));
  }, [filterByTransportista]);

  // Helper to get company by CUIT
  const getCompanyByCuit = useCallback((cuit) => {
    return companies.find(c => c.cuit_rut === cuit);
  }, [companies]);

  // Helper to get company name by ID
  const getCompanyName = useCallback((companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : '-';
  }, [companies]);

  return {
    // Data
    companies,
    trucks,
    trailers,
    drivers,
    users,
    admins,
    loading,
    isTransportista,
    
    // Refresh functions
    fetchData,
    refreshCompanies,
    refreshTrucks,
    refreshTrailers,
    refreshDrivers,
    
    // Helpers
    getCompanyByCuit,
    getCompanyName
  };
};

export default useTransportistas;
